import { contactNumbers } from '@adc/constants'
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import _cx from 'classnames/bind'
import ms from 'ms'
import Button from '../elementals/button'
import Icon from '../elementals/icon'
import Text from '../elementals/text'
import Modal from '../overlays/modal'
import { useApplicationNotifications } from '../applicationNotifiers'
import { ColorNames } from '../../constants/colorVars'
import SparkleAnimatedIcon from '../sparkleAnimatedIcon'
import CognitionMessage from '../cognitionMessage'
import LiveAgentBanner from './liveAgentBanner'
import type {
  CognitionEvaluationChatMessage,
  CognitionEvaluationGroup,
  CognitionEvaluationRole,
  CognitionEvaluationScore,
} from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import { formatPhoneNumber } from '../../utils/formatters'
import type { AutomationTarget } from '../../types'
import type {
  MarkdownComponentAction,
  MarkdownComponentMap,
} from '../elementals/markdownWithComponents/types'
import cls from './styles.scss'

/** Type guard for progress/interim messages that carry a description field */
function isProgressMessage(message: CognitionEvaluationChatMessage): boolean {
  return (
    'description' in message &&
    !!(message as unknown as { description: string }).description
  )
}

/** Type guard for private/system messages (visibility: 'PRIVATE') */
function isPrivateMessage(message: CognitionEvaluationChatMessage): boolean {
  return (
    'visibility' in message &&
    (message as unknown as { visibility: string }).visibility === 'PRIVATE'
  )
}

const ADC_PRIVACY_LINK = 'https://www.auction.com/lp/legal/privacy/'
const ADC_TERMS_LINK = 'https://www.auction.com/lp/legal/terms/'
const DOT_FADE_OUT_DURATION_MS = ms('250')
const DOT_FADE_IN_DURATION_MS = ms('400')

function getDotTransition(
  showDots: boolean,
  hasProgressMessage: boolean
): string {
  if (showDots) return `opacity ${DOT_FADE_IN_DURATION_MS}ms`
  if (hasProgressMessage) return 'none'
  return `all ${DOT_FADE_OUT_DURATION_MS}ms`
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const cx = _cx.bind(cls)

export type AiChatMode = 'chat' | 'mock' | 'trace'
export type AiChatSubmitType = 'buttonPress' | 'enterKey' | 'externalPrompt'

export interface AiChatContextItem {
  urn?: string // Optional URN for existing chats
  url: string
  linkText: string
}

export type AiChatContext = AiChatContextItem[]

export interface AiChatFeedbackHandler {
  (score: CognitionEvaluationScore): Promise<boolean>
}

export interface AiChatResetCallback {
  (): void
}

export interface AiChatEvalGroupUpdateHandler {
  (evaluationGroup: CognitionEvaluationGroup | undefined): void
}

export interface AiChatProps extends AutomationTarget {
  // optional props for customization, exposed via container
  className?: string
  context?: AiChatContext
  externalPrompt?: string
  feedbackPromptTextPositive?: React.ReactNode
  feedbackPromptTextNegative?: React.ReactNode
  feedbackPromptMoreHelpText?: React.ReactNode
  hideChatFeedback?: boolean
  introMessage?: string
  mode?: AiChatMode
  onFeedback?: AiChatFeedbackHandler
  onCopy?: (content: string) => void
  onExternalPromptProcessed?: () => void
  onSend?: (message: string, submitType: AiChatSubmitType) => void
  /** Callback fired when an interactive markdown component triggers an action */
  onAction?: (action: MarkdownComponentAction) => void
  /** Additional or override markdown component registry */
  components?: MarkdownComponentMap
  showContext?: boolean
  showContextAsLink?: boolean
  open?: boolean
  // props handled by container
  messages: CognitionEvaluationChatMessage[]
  isAwaitingReply?: boolean
  disallowPrompting?: boolean
  isLoading?: boolean
  upsertEvaluationGroup: (message: string) => Promise<void>
  resetKey?: number
  showLiveAgentBanner?: boolean
  onLiveAgentClick?: () => void
  onLiveAgentBannerDisplayed?: () => void
}

export const AiChat: React.FC<AiChatProps> = ({
  className,
  context,
  mode = 'chat',
  onFeedback,
  onCopy,
  externalPrompt,
  feedbackPromptMoreHelpText,
  onExternalPromptProcessed,
  onSend,
  onAction,
  components,
  showContext = false,
  showContextAsLink = false,
  hideChatFeedback = false,
  introMessage = 'How may I assist you?',
  open = false,
  upsertEvaluationGroup,
  elmId = 'ai-chat',
  messages = [],
  isAwaitingReply = false,
  disallowPrompting = false,
  isLoading = false,
  resetKey,
  showLiveAgentBanner = false,
  onLiveAgentClick,
  onLiveAgentBannerDisplayed,
}) => {
  const notifications = useApplicationNotifications()
  const [inputValue, setInputValue] = useState('')
  const [savingFeedback, setSavingFeedback] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const feedbackTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Feedback state
  const [isFeedbackPanelOpen, setIsFeedbackPanelOpen] = useState(false)
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false)
  const [chatFeedback, setChatFeedback] = useState<
    'thumbsUp' | 'thumbsDown' | null
  >(null)
  const [commentText, setCommentText] = useState('')

  // Track scroll target (computed synchronously to avoid race with scroll effect)
  // Rules:
  // - Normal case: scroll to last public user message (user sees their Q + response)
  // - If a private or progress message exists after the last public user message: scroll to last message
  //   (covers: private msg visible, progress visible, output-after-private)
  const scrollTargetSequence = useMemo(() => {
    if (messages.length === 0) return -1

    // Find the last public (non-private) user message index
    let lastPublicUserIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (
        messages[i].role === graph.enums.CognitionEvaluationRole.User &&
        !isPrivateMessage(messages[i])
      ) {
        lastPublicUserIdx = i
        break
      }
    }

    // No public user message — scroll to last message
    if (lastPublicUserIdx === -1) {
      return messages[messages.length - 1].sequence
    }

    // Check if any private or progress message exists after the last public user message
    let hasPrivateOrProgressAfterUser = false
    for (let i = lastPublicUserIdx + 1; i < messages.length; i++) {
      if (isPrivateMessage(messages[i]) || isProgressMessage(messages[i])) {
        hasPrivateOrProgressAfterUser = true
        break
      }
    }

    if (hasPrivateOrProgressAfterUser) {
      // Private/progress message scenario — scroll to last message
      return messages[messages.length - 1].sequence
    }

    // Normal case — scroll to last public user message
    return messages[lastPublicUserIdx].sequence
  }, [messages])

  // Message visibility state for animations
  const [messageVisibility, setMessageVisibility] = useState<
    Map<number, boolean>
  >(new Map())

  // Message queue types
  type QueuedMessage = {
    content: string
    source: 'external' | 'internal' // internal is from user input, external is from prop
    id: string
    submitType: AiChatSubmitType
  }

  // Queue state
  const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([])
  const [lastExternalPrompt, setLastExternalPrompt] = useState<string | null>(
    null
  )
  const isSendingRef = useRef(false)

  // Send message
  const sendMessage = useCallback(
    async (message: string) => {
      try {
        if (!message.trim()) return
        // Call upsertEvaluationGroup which returns the full chat with history
        await upsertEvaluationGroup(message)
      } finally {
        // Reset sending flag here as well to ensure it's always cleared
        isSendingRef.current = false
      }
    },
    [upsertEvaluationGroup]
  )

  // Handle external prompt
  useEffect(() => {
    if (externalPrompt && externalPrompt !== lastExternalPrompt) {
      setLastExternalPrompt(externalPrompt)

      // Add to message queue with metadata
      const queuedMessage: QueuedMessage = {
        content: externalPrompt,
        source: 'external',
        id: `external-${Date.now()}`,
        submitType: 'externalPrompt',
      }
      setMessageQueue(prev => [...prev, queuedMessage])
    }
  }, [externalPrompt, lastExternalPrompt])

  // Process message queue
  useEffect(() => {
    // Only process when not loading and not already sending
    if (!isAwaitingReply && !isSendingRef.current && messageQueue.length > 0) {
      // Get the first message from queue
      const [nextMessage] = messageQueue

      // Update queue immediately (functional update to avoid racing with concurrent enqueues)
      setMessageQueue(prev => prev.slice(1))

      // Mark as sending before processing
      isSendingRef.current = true

      // Use async function to properly handle the promise
      const processMessage = async () => {
        try {
          await sendMessage(nextMessage.content)

          // Handle callbacks for external prompts
          if (nextMessage.source === 'external' && onExternalPromptProcessed) {
            onExternalPromptProcessed()
          }
          if (onSend) {
            // this is primarily for analytics tracking
            onSend(nextMessage.content, nextMessage.submitType)
          }
        } finally {
          // The isSendingRef.current = false is handled in sendMessage's finally block
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      processMessage()
    }
  }, [
    isAwaitingReply,
    messageQueue,
    sendMessage,
    onExternalPromptProcessed,
    onSend,
  ])

  // Scroll to dots when loading (but not when progress messages handle scrolling)
  const hasProgressMessage = messages.some(isProgressMessage)
  useEffect(() => {
    // Only scroll when dots appear (isAwaitingReply becomes true)
    // Skip if progress messages are visible — they handle their own scrolling
    if (isAwaitingReply && !hasProgressMessage) {
      // Delay scroll to ensure fade-in animation has started
      const scrollTimeout = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)

      return () => clearTimeout(scrollTimeout)
    }
    return undefined
  }, [isAwaitingReply, hasProgressMessage])

  // Immediate scroll to bottom on mount (when chat opens)
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' })
    }
  }, [])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return undefined
    const resize = () => {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
    // Defer to next frame so scrollHeight is measured after layout has settled.
    // Fixes an over-tall initial render when mounted inside a flex / height:100%
    // container (scrollHeight read on an unsettled first paint clamps to max-height).
    const raf = requestAnimationFrame(resize)
    return () => cancelAnimationFrame(raf)
  }, [inputValue])

  // Focus main textarea when drawer opens
  useEffect(() => {
    if (open && textareaRef.current) {
      // Small delay to ensure the drawer animation has started
      setTimeout(() => {
        textareaRef.current?.focus()
      }, 50)
    }
  }, [open])

  // Focus feedback textarea when feedback modal opens
  useEffect(() => {
    if (isFeedbackPanelOpen && feedbackTextareaRef.current) {
      // Small delay to ensure the modal animation has started
      setTimeout(() => {
        feedbackTextareaRef.current?.focus()
      }, ms('100ms'))
    }
  }, [isFeedbackPanelOpen])

  // Reset chat state function
  const resetChatState = useCallback(() => {
    // Clear input and queues
    setInputValue('')
    setMessageQueue([])
    setMessageVisibility(new Map())

    // Clear feedback state
    setChatFeedback(null)
    setCommentText('')
    setIsFeedbackPanelOpen(false)
    setSavingFeedback(false)

    // Clear refs
    isSendingRef.current = false

    // Clear last external prompt to allow same prompt after reset
    setLastExternalPrompt(null)
  }, [])

  // Watch for resetKey prop changes
  const lastResetKeyRef = useRef(resetKey)
  useEffect(() => {
    if (resetKey !== undefined && resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey
      resetChatState()
    }
  }, [resetKey, resetChatState])

  const handleSend = (submitType: AiChatSubmitType) => {
    if (inputValue.trim()) {
      const message = inputValue.trim()

      // Add to message queue
      const queuedMessage: QueuedMessage = {
        content: message,
        source: 'internal',
        id: `internal-${Date.now()}`,
        submitType,
      }
      setMessageQueue(prev => [...prev, queuedMessage])

      // Clear input immediately for better UX
      setInputValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (!inputValue.trim() || isAwaitingReply || disallowPrompting) return // denied!
      handleSend('enterKey')
    }
  }

  const handleChatFeedback = (feedback: 'thumbsUp' | 'thumbsDown') => {
    // Update selection state
    setChatFeedback(feedback)

    // Open feedback panel immediately (only if not already open)
    if (!isFeedbackPanelOpen) {
      setIsFeedbackPanelOpen(true)
    }
    // If panel already open and user switches, panel stays open

    // Optimistically save score without comment in the background
    if (onFeedback) {
      onFeedback({ score: feedback === 'thumbsUp' }).catch((error: unknown) => {
        // Log error but don't block UI - the user has already seen their selection
        notifications.toast.open({
          message: 'Failed to save feedback score. Please try again.',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      })
    }
  }

  const handleFeedbackPanelSubmit = async () => {
    if (!chatFeedback) return
    setSavingFeedback(true)

    // Save score + comment (notes optional)
    if (onFeedback) {
      await onFeedback({
        score: chatFeedback === 'thumbsUp',
        ...(commentText.trim() ? { notes: commentText } : {}),
      })
    }

    setSavingFeedback(false)
    setIsFeedbackPanelOpen(false)
    // Keep chatFeedback selection and commentText so user sees prior feedback on reopen
  }

  const handleFeedbackPanelClose = () => {
    setIsFeedbackPanelOpen(false)
    // Keep chatFeedback selection visible
    // Keep commentText in case they reopen
  }

  // Manage message visibility for fade-in animations
  useEffect(() => {
    messages.forEach((msg, index) => {
      if (!messageVisibility.has(index)) {
        const isProgress = isProgressMessage(msg)
        const isPrivate = isPrivateMessage(msg)

        if (mode === 'trace' || isProgress || isPrivate) {
          // Trace mode and progress messages: visible immediately
          setMessageVisibility(prev => new Map(prev).set(index, true))
        } else {
          // For chat and mock modes, use fade-in animation
          // New message - set initial visibility to false
          setMessageVisibility(prev => new Map(prev).set(index, false))

          // Calculate delay based on mode and index
          const baseDelay = mode === 'mock' ? index * 400 : 50

          // After a delay, set to true to trigger fade-in
          setTimeout(() => {
            setMessageVisibility(prev => new Map(prev).set(index, true))
          }, baseDelay)
        }
      }
    })
  }, [messages, messageVisibility, mode])

  // Scroll when new content arrives (output, progress, or private messages)
  const lastScrollTriggerRef = useRef(-1)
  const lastMessageCountRef = useRef(0)
  const wasAwaitingRef = useRef(false)
  useEffect(() => {
    if (messages.length === 0) return

    const lastSeq = messages[messages.length - 1].sequence
    const hasNewContent =
      lastSeq > lastScrollTriggerRef.current ||
      messages.length !== lastMessageCountRef.current ||
      hasProgressMessage

    // Detect when reply completes (isAwaitingReply transitions false)
    const replyJustCompleted = wasAwaitingRef.current && !isAwaitingReply
    wasAwaitingRef.current = isAwaitingReply

    if (hasNewContent || replyJustCompleted) {
      lastScrollTriggerRef.current = lastSeq
      lastMessageCountRef.current = messages.length
      setTimeout(() => {
        if (replyJustCompleted && !hasProgressMessage) {
          // Reply finished: scroll to target (user message + response visible)
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
          }
        } else {
          // During streaming/progress: scroll to absolute bottom
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop =
              messagesContainerRef.current.scrollHeight
          }
        }
      }, 100)
    }
  }, [messages, hasProgressMessage, isAwaitingReply])

  // Hide dots when a progress or active private message is visible (avoids double-indicator jank)
  const lastMessageIsPrivate =
    messages.length > 0 && isPrivateMessage(messages[messages.length - 1])
  const showDots =
    isAwaitingReply && !hasProgressMessage && !lastMessageIsPrivate

  return (
    <div className={cx('container', className)} data-elm-id={elmId}>
      {showLiveAgentBanner && onLiveAgentClick && (
        <LiveAgentBanner
          onClickLiveAgent={onLiveAgentClick}
          onBannerDisplayed={onLiveAgentBannerDisplayed}
          elmId={`${elmId}-live-agent-banner`}
        />
      )}
      <div
        className={cx('messages-container')}
        ref={messagesContainerRef}
        data-elm-id={`${elmId}-messages`}
      >
        {messages.length === 0 && !isLoading && (
          <div className={cx('intro-message')}>
            <Text.Header level={1} color='gray'>
              {introMessage}
            </Text.Header>
          </div>
        )}
        {messages.map((message, index) => (
          <div
            key={`${message.sequence}-${message.role}`}
            ref={
              scrollTargetSequence === message.sequence ? messagesEndRef : null
            }
          >
            <CognitionMessage
              message={message}
              index={index}
              previousMessageRole={
                index > 0 ? messages[index - 1].role ?? undefined : undefined
              }
              isLastMessage={index === messages.length - 1}
              onCopy={onCopy}
              onAction={onAction}
              components={components}
              mode={mode}
              isVisible={messageVisibility.get(index) || false}
            />
          </div>
        ))}
        <div
          className={cx('message', 'ai', {
            'loading-hidden': !showDots,
          })}
          /**
           * This chunk of UI magic is to have the dots fade in and out smoothly
           * without causing layout shifts in the chat messages area.  We achieve this by
           * always reserving the space for the dots container, but adjusting its
           * height, margin, opacity, and visibility based on whether we're
           * actually awaiting a reply. The transition on opacity creates the
           * fade effect.
           */
          style={{
            marginTop: showDots ? '0px' : '-66px',
            height: showDots ? '30px' : '0px',
            opacity: showDots ? 1 : 0,
            visibility: showDots ? 'visible' : 'hidden',
            transition: getDotTransition(
              showDots,
              hasProgressMessage || lastMessageIsPrivate
            ),
          }}
        >
          <div className={cx('assistant-label')}>
            <SparkleAnimatedIcon isActive={showDots} />
            <span>AI Assistant</span>
          </div>
          <div className={cx('loading')}>
            <div className={cx('dots')}>
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </div>

      <div
        className={cx('input-container')}
        data-elm-id={`${elmId}-input-container`}
      >
        {messageQueue.length > 0 && (
          <div
            className={cx('queue-indicator')}
            data-elm-id={`${elmId}-queue-indicator`}
          >
            <Icon name='clock' iconStyle='regular' color='gray' size='small' />
            <Text size='mini' color='gray'>
              {messageQueue.length} message{messageQueue.length > 1 ? 's' : ''}{' '}
              pending...
            </Text>
          </div>
        )}
        {mode === 'chat' &&
          !hideChatFeedback &&
          messages.some(
            m =>
              m.role ===
              (graph.enums.CognitionEvaluationRole
                .Model as CognitionEvaluationRole)
          ) && (
            <div className={cx('chat-feedback-container')}>
              <div className={cx('chat-feedback')}>
                <Text size='tiny' color='gray' style={{ textAlign: 'right' }}>
                  Rate conversation:
                </Text>
                <Button
                  className={cx('extra-small-button', 'feedback-button')}
                  role='secondary'
                  color={
                    chatFeedback === 'thumbsUp'
                      ? ColorNames.Action
                      : ColorNames.Gray
                  }
                  icon={
                    <Icon
                      name='thumbs-up'
                      iconStyle={
                        chatFeedback === 'thumbsUp' ? 'solid' : 'regular'
                      }
                      color={chatFeedback === 'thumbsUp' ? 'success' : 'gray'}
                    />
                  }
                  isSelected={chatFeedback === 'thumbsUp'}
                  onClick={() => handleChatFeedback('thumbsUp')}
                  size='small'
                  elmId={`${elmId}-thumbs-up`}
                  collapse
                />
                <Button
                  className={cx('extra-small-button', 'feedback-button')}
                  role='secondary'
                  color={
                    chatFeedback === 'thumbsDown'
                      ? ColorNames.Action
                      : ColorNames.Gray
                  }
                  icon={
                    <Icon
                      name='thumbs-down'
                      iconStyle={
                        chatFeedback === 'thumbsDown' ? 'solid' : 'regular'
                      }
                      color={chatFeedback === 'thumbsDown' ? 'error' : 'gray'}
                    />
                  }
                  isSelected={chatFeedback === 'thumbsDown'}
                  onClick={() => handleChatFeedback('thumbsDown')}
                  size='small'
                  elmId={`${elmId}-thumbs-down`}
                  collapse
                />
              </div>
            </div>
          )}
        {showContext && context && context.length > 0 && mode !== 'mock' && (
          <div
            className={cx('context-banner')}
            data-elm-id={`${elmId}-context`}
          >
            <Icon name='link' iconStyle='solid' color='inherit' size='small' />
            <span className={cx('context-label')}>Context: </span>
            {context.map((contextItem, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <span className={cx('context-separator')}>, </span>
                )}
                {showContextAsLink ? (
                  <a
                    href={contextItem.url}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={cx('context-link')}
                  >
                    {contextItem.linkText}
                  </a>
                ) : (
                  <span className={cx('context-text')}>
                    {contextItem.linkText}
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
        {mode === 'chat' && (
          <>
            <Modal
              title='Additional feedback'
              fullScreen='never'
              slideUp='mobileOnly'
              size='tiny'
              open={isFeedbackPanelOpen}
              onClose={handleFeedbackPanelClose}
              className={cx('feedback-modal')}
              data-elm-id={`${elmId}-feedback-modal`}
            >
              <Modal.Content>
                <Text size='mini' color='grayMedium' weight='medium'>
                  Your rating:
                </Text>
                <div className={cx('feedback-modal-buttons')}>
                  <Button
                    role='secondary'
                    color={
                      chatFeedback === 'thumbsUp'
                        ? ColorNames.Action
                        : ColorNames.Gray
                    }
                    size='medium'
                    isSelected={chatFeedback === 'thumbsUp'}
                    icon={
                      <Icon
                        name='thumbs-up'
                        iconStyle={
                          chatFeedback === 'thumbsUp' ? 'solid' : 'regular'
                        }
                        color={chatFeedback === 'thumbsUp' ? 'success' : 'gray'}
                        className={cx('feedback-modal-thumb-icon')}
                      />
                    }
                    onClick={() => setChatFeedback('thumbsUp')}
                    elmId={`${elmId}-feedback-modal-thumbs-up`}
                    collapse
                  />
                  <Button
                    role='secondary'
                    color={
                      chatFeedback === 'thumbsDown'
                        ? ColorNames.Action
                        : ColorNames.Gray
                    }
                    size='medium'
                    isSelected={chatFeedback === 'thumbsDown'}
                    icon={
                      <Icon
                        name='thumbs-down'
                        iconStyle={
                          chatFeedback === 'thumbsDown' ? 'solid' : 'regular'
                        }
                        color={chatFeedback === 'thumbsDown' ? 'error' : 'gray'}
                        className={cx('feedback-modal-thumb-icon')}
                      />
                    }
                    onClick={() => setChatFeedback('thumbsDown')}
                    elmId={`${elmId}-feedback-modal-thumbs-down`}
                    collapse
                  />
                </div>
                <Text size='tiny' color='gray'>
                  Please provide any other feedback you have around your current
                  experience.
                </Text>
                <textarea
                  ref={feedbackTextareaRef}
                  className={cx('feedback-modal-textarea')}
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  placeholder=''
                  rows={1}
                  data-elm-id={`${elmId}-feedback-modal-textarea`}
                />

                <div
                  className={cx('feedback-modal-divider')}
                  aria-hidden='true'
                />

                <Button
                  className={cx('feedback-modal-submit')}
                  role='primary'
                  size='large'
                  onClick={handleFeedbackPanelSubmit}
                  disabled={savingFeedback}
                  loading={savingFeedback}
                  elmId={`${elmId}-feedback-submit`}
                >
                  Submit Feedback
                </Button>

                <div className={cx('feedback-modal-more-help')}>
                  Need more help? Call{' '}
                  {feedbackPromptMoreHelpText ?? (
                    <a href={`tel:${contactNumbers.ONLINE_BUYER_EXPERIENCE}`}>
                      {formatPhoneNumber(
                        contactNumbers.ONLINE_BUYER_EXPERIENCE
                      )}
                    </a>
                  )}
                </div>
              </Modal.Content>
            </Modal>
            <div className={cx('input-wrapper')}>
              <textarea
                ref={textareaRef}
                className={cx('input')}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Ask a question...'
                rows={1}
                data-elm-id={`${elmId}-input`}
              />
              <div className={cx('send-button')}>
                <Button
                  role='primary'
                  icon={
                    <Icon name='arrow-up' iconStyle='solid' color='white' />
                  }
                  onClick={() => handleSend('buttonPress')}
                  size='small'
                  elmId='ai-chat-send'
                  collapse
                  disabled={
                    !inputValue.trim() || isAwaitingReply || disallowPrompting
                  }
                />
              </div>
            </div>
          </>
        )}
        {mode === 'chat' && (
          <div className={cx('disclaimer')}>
            <Text size='tiny' color='gray' style={{ textAlign: 'center' }}>
              By chatting, you agree to this{' '}
              <a
                href='#'
                onClick={e => {
                  e.preventDefault()
                  setIsDisclaimerModalOpen(true)
                }}
              >
                disclaimer
              </a>
              .
            </Text>
          </div>
        )}
      </div>
      {/* Disclaimer Modal */}
      <Modal
        title='Disclaimer'
        fullScreen='never'
        slideUp='always'
        open={isDisclaimerModalOpen}
        onClose={() => setIsDisclaimerModalOpen(false)}
        data-elm-id='disclaimer-modal'
      >
        <Modal.Content style={{ paddingBottom: '18px' }}>
          <Text size='small' color='grayDark' as='span'>
            I am an AI assistant tool. Do not share sensitive personal data, as
            chats are monitored and retained for quality and training purposes.
            Responses may be inaccurate or incomplete. By using our site and
            this tool, you agree to our
          </Text>{' '}
          <a
            data-elm-id='privacy_statement'
            href={ADC_PRIVACY_LINK}
            target='_blank'
            rel='noreferrer'
            style={{ fontSize: '16px' }}
          >
            Privacy Statement
          </a>{' '}
          and{' '}
          <a
            data-elm-id='terms_of_use'
            href={ADC_TERMS_LINK}
            target='_blank'
            rel='noreferrer'
            style={{ fontSize: '16px' }}
          >
            Terms of Use
          </a>
          .
        </Modal.Content>
      </Modal>
    </div>
  )
}

export default AiChat
